import { IfAbility } from '@efdigital/ll-addon-auth';
import { Heading } from '@efdigital/ll-addon-ui';
import { CoachingQuotaLedgers } from '@efdigital/ll-admin-v2/components/coaching/sessions/coaching-quota-ledgers.gts';
import { CoachingSessionDetail } from '@efdigital/ll-admin-v2/components/coaching/sessions/coaching-session-detail.gts';
import SessionAdminAlerts from '@efdigital/ll-admin-v2/components/coaching/sessions/detail/session-admin-alerts.gts';
import { SessionQuotaGrantModal } from '@efdigital/ll-admin-v2/components/coaching/sessions/detail/session-quota-grant-modal.gts';
import { queryQuotaLedgersForSession } from '@efdigital/ll-admin-v2/data/builders/coaching/coaching-quota-ledgers';
import type { CoachingSessionsSessionModel } from '@efdigital/ll-admin-v2/routes/coaching/sessions/session';
import type AbilityService from '@efdigital/ll-admin-v2/services/ability';
import { on } from '@ember/modifier';
import { LinkTo } from '@ember/routing';
import type RouterService from '@ember/routing/router-service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { Request } from '@warp-drive/ember';

export default class SessionsSessionRoute extends Component<{
  Args: {
    model: CoachingSessionsSessionModel;
  };
}> {
  @service declare ability: AbilityService;
  @service declare router: RouterService;

  get canEditSessions() {
    return this.ability.canEdit('coaching-sessions');
  }

  get queryQuotaLedgers() {
    return queryQuotaLedgersForSession(this.args.model.coachingSession.id!);
  }

  refreshSession = async () => {
    await this.router.refresh('coaching.sessions.session');
  };

  handleSessionDeleted = async () => {
    await this.router.transitionTo('coaching.sessions.index');
  };

  <template>
    <div class="container py-5">
      <Heading @text="Session details" class="text-start px-0 mb-4">
        <LinkTo @route="coaching.sessions.index" class="btn btn-primary">
          <iconify-icon icon="ph:arrow-left-bold" inline />
          Back to sessions
        </LinkTo>
      </Heading>

      <SessionAdminAlerts
        @coachingSession={{@model.coachingSession}}
        @coachingPlan={{@model.coachingPlan}}
        @coachingPartnership={{@model.coachingPartnership}}
        @cohort={{@model.cohort}}
        @cohortProgramId={{@model.cohortProgramId}}
      />

      <CoachingSessionDetail
        @coachingSession={{@model.coachingSession}}
        @coachProfile={{@model.coachProfile}}
        @coachingPlan={{@model.coachingPlan}}
        @coachingPartnership={{@model.coachingPartnership}}
        @cohort={{@model.cohort}}
        @refresh={{this.refreshSession}}
        @abandoned={{this.handleSessionDeleted}}
      />

      <Request @query={{this.queryQuotaLedgers}}>
        <:content as |content state|>
          <div class="card rounded-4">
            <h3 class="card-header h5 py-3">Quota ledger</h3>
            <div class="card-body">
              <CoachingQuotaLedgers
                @quotaLedgers={{content.data}}
                @sessionType={{@model.coachingSession.sessionType}}
                class="mb-4"
              />

              <IfAbility @ability="coaching-sessions" @atLeast="editor">
                <:if>
                  <SessionQuotaGrantModal
                    @coachingPlan={{@model.coachingPlan}}
                    @coachingSession={{@model.coachingSession}}
                    @onSuccess={{state.refresh}}
                  >
                    <:grant as |showModal|>
                      <button
                        type="button"
                        class="btn btn-primary"
                        {{on "click" showModal}}
                      >Grant quota...</button>
                    </:grant>
                  </SessionQuotaGrantModal>
                </:if>
              </IfAbility>
            </div>
          </div>
        </:content>
      </Request>
    </div>
  </template>
}
